import { useMemo } from 'react'

import { useQueries, useQueryClient } from '@tanstack/react-query'
import { compact, mapValues, sumBy } from 'lodash'

import { SupportedChainId } from '~/constants/chains'
import { QUERY_KEYS } from '~/constants/react-query'
import {
  REFETCH_BALANCES,
  REFETCH_SINGLE_CHAIN_BALANCES,
} from '~/constants/refetch-intervals'
import { useActiveOrg, useActiveOrgId } from '~/hooks/org'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import {
  ServerGetRequestFunction,
  useServerRequests,
} from '~/hooks/useRequests'
import { useLoadMultipleTokenMetadata } from '~/hooks/useTokenMetadata'
import { IAddress } from '~/types'
import { TokenBalance } from '~/utils/account'
import { EvmAddress } from '~/utils/address'

type RawOrgAccountBalancesResponse = {
  balances: Array<Omit<TokenBalance, 'amount'> & { amount: string }>
  totalValue: number
}

type BalancesResponse = {
  balances: TokenBalance[]
  totalValue: number
}

// Historical balances still use the nested account response shape.
export type RawOrgBalancesResponse = {
  balances: {
    [address: string]: RawOrgAccountBalancesResponse
  }
  totalValue: number
}

type OrgBalancesResponse = {
  balances: {
    [address: string]: BalancesResponse
  }
  totalValue: number
}

type RawBalanceRow = {
  address: EvmAddress.Type
  chainId: SupportedChainId
  decimals: number
  symbol: string
  spam: boolean
  whitelisted: boolean
  price: { usd?: number }
  balance: string
  value: { usd?: number }
}

export const getSmartAccountBalances = async ({
  serverGetRequest,
  address,
  showSpamTokens,
  chainIds,
  orgId,
}: {
  serverGetRequest: ServerGetRequestFunction
  address: EvmAddress.Type
  showSpamTokens: boolean
  chainIds?: number[]
  orgId: string | undefined
}): Promise<BalancesResponse> => {
  // An explicit empty list means no networks; omission uses the server default.
  if (chainIds?.length === 0) return { balances: [], totalValue: 0 }

  const response = await serverGetRequest<{ data: RawBalanceRow[] }>({
    path: `/api/smart_accounts/${address}/balances/v2`,
    params: {
      ...(chainIds && { chainIds: chainIds.join(',') }),
      ...(orgId && { orgId }),
    },
    // Rethrow so useQueryWrapper sees the 404 and stops polling a missing account
    onError: (error) => {
      throw error
    },
  })

  if (!response) throw new Error('Failed to fetch account balances')

  return transformBalanceRows({
    ownerAddress: address,
    rows: response.data.data,
    showSpamTokens,
  })
}

const transformBalanceRows = ({
  ownerAddress,
  rows,
  showSpamTokens,
}: {
  ownerAddress: EvmAddress.Type
  rows: RawBalanceRow[]
  showSpamTokens: boolean
}): BalancesResponse => {
  const balances = rows
    .filter((row) => showSpamTokens || !row.spam)
    .map((row): TokenBalance => ({
      ownerAddress,
      amount: BigInt(row.balance),
      chainId: row.chainId,
      address: row.address,
      usdValue: row.value.usd ?? 0,
      price: row.price.usd,
      whitelisted: row.whitelisted,
      tokenMetadata: {
        symbol: row.symbol,
        decimals: row.decimals,
        chainId: row.chainId,
        address: row.address,
      },
    }))

  return { balances, totalValue: sumBy(balances, 'usdValue') }
}

const getOrgBalances = async ({
  serverGetRequest,
  orgId,
  showSpamTokens,
  chainIds,
}: {
  serverGetRequest: ServerGetRequestFunction
  orgId: string
  showSpamTokens: boolean
  chainIds: number[]
}): Promise<OrgBalancesResponse['balances']> => {
  if (chainIds.length === 0) return {}

  const response = await serverGetRequest<{
    data: Record<string, RawBalanceRow[]>
  }>({
    path: '/api/org/balances/v2',
    params: { orgId, chainIds: chainIds.join(',') },
  })

  if (!response) throw new Error('Failed to fetch org balances')

  return mapValues(response.data.data, (rows, address) =>
    transformBalanceRows({
      ownerAddress: EvmAddress.format(address),
      rows,
      showSpamTokens,
    }),
  )
}

export const useSmartAccountBalances = (
  address?: IAddress,
  options: {
    showSpamTokens?: boolean
    chainId?: number
    /** Read across workspaces without applying the active org's networks or spam overrides. */
    allChains?: boolean
  } = {},
) => {
  const { serverGetRequest } = useServerRequests()
  const loadTokenMetadata = useLoadMultipleTokenMetadata()
  const { org, isLoading: isOrgLoading } = useActiveOrg()
  const activeOrgId = useActiveOrgId()

  const { showSpamTokens = false, chainId, allChains = false } = options
  const orgId = allChains ? undefined : activeOrgId
  const chainIds =
    chainId !== undefined
      ? [chainId]
      : allChains
        ? undefined
        : (org?.smartAccountsChainIds ?? (activeOrgId ? [] : undefined))

  const refetchInterval =
    chainId !== undefined ? REFETCH_SINGLE_CHAIN_BALANCES : REFETCH_BALANCES

  const { data, isLoading, refetch } = useQueryWrapper<BalancesResponse>({
    // version keeps v2 rows in their own cache entries, so they never mix
    // with old-endpoint entries (including rows seeded by useOrgBalances)
    queryKey: [
      QUERY_KEYS.SMART_ACCOUNT_BALANCES,
      address,
      {
        showSpamTokens,
        version: '2.0',
        orgId,
        chainIds,
      },
    ],
    queryFn: async () => {
      if (!address) return { balances: [], totalValue: 0 }

      const result = await getSmartAccountBalances({
        serverGetRequest,
        address: EvmAddress.format(address),
        showSpamTokens,
        chainIds,
        orgId,
      })

      loadTokenMetadata(
        compact(result.balances.map((balance) => balance.tokenMetadata)),
      )

      return result
    },
    refetchInterval,
    enabled: !!address && chainIds?.length !== 0,
  })

  return {
    isLoading:
      isLoading ||
      (!!address && chainId === undefined && !allChains && isOrgLoading),
    balances: data?.balances ?? [],
    totalValue: data?.totalValue ?? 0,
    refetch,
  }
}

export const useMultipleSmartAccountBalances = (
  addresses: IAddress[],
  showSpamTokens = false,
  { allChains = false }: { allChains?: boolean } = {},
) => {
  const { serverGetRequest } = useServerRequests()
  const loadTokenMetadata = useLoadMultipleTokenMetadata()
  const { org, isLoading: isOrgLoading } = useActiveOrg()
  const activeOrgId = useActiveOrgId()

  const orgId = allChains ? undefined : activeOrgId
  const chainIds = allChains
    ? undefined
    : (org?.smartAccountsChainIds ?? (activeOrgId ? [] : undefined))

  const balanceQueries = addresses.map((address) => ({
    // Same key shape as useSmartAccountBalances so both share cache entries
    queryKey: [
      QUERY_KEYS.SMART_ACCOUNT_BALANCES,
      address,
      {
        showSpamTokens,
        version: '2.0',
        orgId,
        chainIds,
      },
    ],
    queryFn: async () => {
      const balances = await getSmartAccountBalances({
        serverGetRequest,
        address: EvmAddress.format(address),
        showSpamTokens,
        chainIds,
        orgId,
      })

      loadTokenMetadata(
        compact(balances.balances.map((balance) => balance.tokenMetadata)),
      )

      return balances
    },
    refetchInterval: REFETCH_BALANCES,
    enabled: chainIds?.length !== 0,
  }))

  const results = useQueries({
    queries: balanceQueries,
  })

  const isLoading =
    (addresses.length > 0 && !allChains && isOrgLoading) ||
    results.some((result) => result.isLoading)
  const refetchAll = () => results.forEach((result) => result.refetch())

  const balances = useMemo(
    () =>
      addresses.reduce<{
        [address: string]: {
          balances: TokenBalance[]
          totalValue: number
        }
      }>((acc, address, index) => {
        const result = results[index]
        if (result.data) {
          acc[address] = {
            balances: result.data.balances,
            totalValue: result.data.totalValue,
          }
        }
        return acc
      }, {}),
    [addresses, results],
  )

  return {
    isLoading,
    balances,
    refetch: refetchAll,
  }
}

export const useOrgBalances = (
  orgId: string | undefined,
  showSpamTokens = false,
) => {
  const queryClient = useQueryClient()
  const { serverGetRequest } = useServerRequests()
  const loadTokenMetadata = useLoadMultipleTokenMetadata()
  const { org, isLoading: isOrgLoading } = useActiveOrg()

  const chainIds = org?.smartAccountsChainIds ?? []

  const { data, isLoading, isError, refetch } =
    useQueryWrapper<OrgBalancesResponse>({
      // version keeps v2 entries in their own cache entries, so they never
      // mix with old-endpoint entries
      queryKey: [
        QUERY_KEYS.ORG_BALANCES,
        orgId,
        { showSpamTokens, version: '2.0', chainIds },
      ],
      queryFn: async () => {
        if (!orgId) throw new Error('No orgId')

        const balances = await getOrgBalances({
          serverGetRequest,
          orgId,
          showSpamTokens,
          chainIds,
        })

        loadTokenMetadata(
          compact(
            Object.values(balances)
              .flatMap((balance) => balance.balances)
              .map((balance) => balance.tokenMetadata),
          ),
        )

        const totalValue = sumBy(
          Object.values(balances),
          (balance) => balance.totalValue,
        )

        // Seed the QUERY_KEYS.SMART_ACCOUNT_BALANCES cache under the same key
        // shape useSmartAccountBalances reads from
        Object.entries(balances).forEach(([address, balance]) => {
          queryClient.setQueryData(
            [
              QUERY_KEYS.SMART_ACCOUNT_BALANCES,
              address,
              {
                showSpamTokens,
                version: '2.0',
                orgId,
                chainIds,
              },
            ],
            balance,
          )
        })

        return { balances, totalValue }
      },
      enabled: !!orgId && chainIds.length > 0,
      refetchInterval: REFETCH_BALANCES,
    })

  return {
    isLoading: isLoading || (!!orgId && isOrgLoading),
    isError,
    balances: data?.balances ?? {},
    totalValue: data?.totalValue ?? 0,
    refetch,
  }
}
